import {
  ListItemContent,
  NormalizedRecommendation,
  RecommendationAdapter,
  RecommendationTypeHandler,
} from "../types";
import { RoadmapItemSourceType } from "../../../../../../customer-experience/src/lib/ShiOneClient";
import CloudIcon from "@mui/icons-material/Cloud";
import { AwsListItemAdditionalContent } from "../../components/aws/AwsListItemAdditionalContent";
import { AwsRecommendationDetailsDialog } from "../../components/aws/AwsRecommendationDetailsDialog";
import {
  getAwsCategoryDisplayLabel,
  getAwsOrderedCategories,
} from "../../utils/awsRecommendationsCategoryMappings";
import { AwsRecommendationSummary } from "../../../../../../customer-experience/src/v2/src/features/recommendations-feature/utils/recommendationTypes";

const awsAdapter: RecommendationAdapter<AwsRecommendationSummary> = {
  normalize: (rec: AwsRecommendationSummary): NormalizedRecommendation => {
    const searchableFields = [
      rec.category,
      rec.impact,
      rec.problem,
      rec.solution,
      ...(rec.affectedScopes?.flatMap(
        (sub) =>
          sub.affectedResources?.map((r) =>
            [r.resourceName, r.resourceType].filter(Boolean).join(" "),
          ) || [],
      ) || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return {
      id: rec.recommendationId || "",
      sourceId: `aws:${rec.category}:${rec.recommendationId}:${rec.customerId || "unknown"}`,
      sourceType: "aws",
      title: rec.problem || "",
      description: rec.solution || "",
      category: rec.category || "Uncategorized",
      searchableText: searchableFields,
      metadata: {
        impact: rec.impact,
        totalAffectedScopes: rec.totalAffectedScopes,
        totalAffectedResources: rec.totalAffectedResources,
        customerId: rec.customerId,
      },
      originalData: rec,
      isLinkedToRoadmap: rec.isLinkedToRoadmap || false,
    };
  },

  getSourceId: (rec: AwsRecommendationSummary) =>
    `aws:${rec.category}:${rec.recommendationId}:${rec.customerId}`,

  getTitle: (rec: AwsRecommendationSummary) =>
    rec.problem || rec.solution || "AWS Recommendation",

  getDescription: (rec: AwsRecommendationSummary) =>
    rec.solution || rec.problem || "",
};

export const awsRecommendationHandler: RecommendationTypeHandler<AwsRecommendationSummary> =
  {
    sourceType: RoadmapItemSourceType.AwsCloudRecommendation,

    displayConfig: {
      icon: CloudIcon,
      iconColor: "text.secondary",
      label: "AWS Recommendation",
      showDescription: false,
    },

    adapter: awsAdapter,

    detailDialog: AwsRecommendationDetailsDialog,

    createMetadata: (recommendation: AwsRecommendationSummary) => {
      const awsRecommendationData = {
        id: `aws:${recommendation.category}:${recommendation.recommendationId}:${recommendation.customerId}`,
        recommendationId: (recommendation as any).recommendationId,
        recommendationTypeId: recommendation.recommendationTypeId,
        customerId: recommendation.customerId,
        category: recommendation.category,
        impact: recommendation.impact,
        problem: recommendation.problem,
        solution: recommendation.solution,
        totalAffectedScopes: recommendation.totalAffectedScopes,
        totalAffectedResources: recommendation.totalAffectedResources,
        securitySpecificMetadata:
          recommendation.sourceMetadata?.securitySpecificMetadata,
        affectedScopes: recommendation.affectedScopes,
        aggregatedRecommendation: true,
      };

      return {
        AwsRecommendation: JSON.stringify(awsRecommendationData),
      };
    },

    isType: (
      recommendation: any,
    ): recommendation is AwsRecommendationSummary => {
      return (
        recommendation.sourceType ===
        RoadmapItemSourceType.AwsCloudRecommendation
      );
    },

    sourceSection: {
      sectionLabel: "AWS RECOMMENDATIONS",
      getSources: (recommendations: AwsRecommendationSummary[]) => {
        const categoryCounts = recommendations.reduce(
          (acc, rec) => {
            const category = rec.category || "";
            acc[category] = (acc[category] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        );

        return getAwsOrderedCategories()
          .filter((category) => categoryCounts[category] > 0)
          .map((category) => ({
            id: category,
            label: getAwsCategoryDisplayLabel(category),
            secondaryLabel: `${categoryCounts[category]} Recommendation${
              categoryCounts[category] !== 1 ? "s" : ""
            }`,
            count: categoryCounts[category],
          }));
      },
      getSourceItems: (
        recommendations: AwsRecommendationSummary[],
        sourceId: string,
      ) => {
        return recommendations.filter((rec) => rec.category === sourceId);
      },
    },

    getListItemContent: (
      recommendation: AwsRecommendationSummary,
    ): ListItemContent => {
      const scopeCount = recommendation.totalAffectedScopes || 0;
      const resourceCount = recommendation.totalAffectedResources || 0;
      const categoryLabel = getAwsCategoryDisplayLabel(
        recommendation.category || "",
      );
      const baseText = `${categoryLabel} - ${recommendation.impact || ""}`;

      const counts = [];
      if (scopeCount > 0) {
        counts.push(`${scopeCount} scope${scopeCount !== 1 ? "s" : ""}`);
      }
      if (resourceCount > 0) {
        counts.push(
          `${resourceCount} resource${resourceCount !== 1 ? "s" : ""}`,
        );
      }

      const secondary =
        counts.length > 0
          ? `${baseText} (${counts.join(", ")} affected)`
          : baseText;

      return {
        primary:
          recommendation.problem ||
          recommendation.solution ||
          "AWS Recommendation",
        secondary,
      };
    },

    getChipLabel: (recommendation: AwsRecommendationSummary): string => {
      const label =
        recommendation.problem ||
        recommendation.solution ||
        "AWS Recommendation";
      const scopeCount = recommendation.totalAffectedScopes || 0;
      const resourceCount = recommendation.totalAffectedResources || 0;

      if (scopeCount > 0 || resourceCount > 0) {
        const parts = [];
        if (scopeCount > 0) {
          parts.push(`${scopeCount} scope${scopeCount !== 1 ? "s" : ""}`);
        }
        if (resourceCount > 0) {
          parts.push(`${resourceCount} res`);
        }
        return `${label} (${parts.join(", ")})`;
      }

      return label;
    },

    getGroupKey: (recommendation: AwsRecommendationSummary): string => {
      return `aws-${recommendation.category || "uncategorized"}`;
    },

    getGroupTitle: (groupKey: string): string => {
      if (groupKey.startsWith("aws-")) {
        const category = groupKey.replace("aws-", "");
        return `AWS - ${getAwsCategoryDisplayLabel(category)}`;
      }
      return groupKey;
    },

    ListItemAdditionalContent: AwsListItemAdditionalContent,
  };
